/**
 * CurrentOperatorModal — 一時対応者選択モーダル
 *
 * 10秒無操作時に自動表示され、本日勤務中のスタッフをカードで一覧表示。
 * カードをクリックすると onSelect が発火、それ以外をクリック/ESC で onCancel 発火。
 *
 * フェードイン5秒でゆっくり表示（利用者が気付かないうちに強制表示しないように）。
 */
import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Paper, Typography,
} from '@mui/material';
import { isOnDuty, isFixedStaff } from '../utils/staffPriority';

interface StaffRecord {
  id: number;
  staff_name: string;
  staff_login_id?: string;
  bg_color?: string | null;
  text_color?: string | null;
  priority?: number | null;
}

interface Props {
  open: boolean;
  staffList: StaffRecord[];
  onSelect: (staff: StaffRecord) => void;
  onCancel: () => void;
}

export default function CurrentOperatorModal({ open, staffList, onSelect, onCancel }: Props) {
  const onDutyStaff = staffList
    .filter(s => isOnDuty(s.priority as any))
    .filter(s => !isFixedStaff(s));

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      transitionDuration={{ enter: 5000, exit: 200 }}
      // 他のモーダル(チャット・ログイン等)の入力を邪魔しないため focus trap を無効化
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <DialogTitle sx={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: 700 }}>
        対応者？
      </DialogTitle>
      <DialogContent sx={{ pb: 4 }}>
        {onDutyStaff.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">本日勤務のスタッフがいません</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            {onDutyStaff.map((staff) => {
              // staff の bg_color / text_color があれば優先適用、なければデフォルト
              const bg = (staff.bg_color && String(staff.bg_color).trim()) || '#e8f5e9';
              const fg = (staff.text_color && String(staff.text_color).trim()) || '#2e7d32';
              return (
                <Paper
                  key={staff.id}
                  elevation={2}
                  onClick={() => onSelect(staff)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: bg,
                    color: fg,
                    minHeight: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 6,
                    },
                    userSelect: 'none',
                    px: 1,
                  }}
                >
                  {staff.staff_name}
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
